import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock';
import { up, down } from 'slide-element';
import clickOutside from 'click-outside';
import { createFocusTrap } from 'focus-trap';
import Cookies from 'js-cookie';

const $body = document.querySelector('body');
const $searchPanelBtn = document.querySelectorAll('.btn-search-panel');
const $searchPanelCloseBtn = document.querySelector('.btn-close-search-panel');
const $searchPanel = document.querySelector('.search-panel');
const $searchInput = document.querySelector('input[type="search"]');
const $suggestionsList = document.querySelector('.suggestions-search-list');
const $suggestionsSection = document.querySelector('.suggestions-search');
const $lastSearchSection = document.querySelector('.last-search');
const $lastSearchList = document.querySelector('.last-search-list');
const $searchBtn = document.querySelector('.search-input-wrapper button');

let unbindClickOutside;
let focusTrap;
let searchTimeout;

const collapseSearchPanel = () => {
    // Remove overlay
    $body.classList.remove('with-overlay');

    // clickOutside
    if (unbindClickOutside) {
        unbindClickOutside();
        unbindClickOutside = null;
    }

    // Deactivate focus trap
    focusTrap.deactivate();

    $searchPanel.setAttribute('aria-hidden', 'true');
    $searchPanelBtn.forEach((btn) => {
        btn.setAttribute('aria-expanded', 'false');
    });

    // unlock Body scroll
    enableBodyScroll($searchPanel);

    // Close panel
    up($searchPanel);
};

function onEscape(event) {
    if (event.key === 'Escape') {
        collapseSearchPanel();
        window.removeEventListener('keydown', onEscape);
    }
}

const expandSearchPanel = () => {
    // Add overlay
    $body.classList.add('with-overlay');

    document.dispatchEvent(new CustomEvent('fadoq:searchPanelOpened'));

    // clickOutside
    unbindClickOutside = clickOutside($searchPanel, () => {
        collapseSearchPanel();
    });

    // Initialize and activate focus trap
    setTimeout(() => {
        focusTrap = createFocusTrap($searchPanel, {
            clickOutsideDeactivates: true,
            preventScroll: true,
        });
        focusTrap.activate();
    }, 100);

    $searchPanel.setAttribute('aria-hidden', 'false');
    $searchPanelBtn.forEach((btn) => {
        btn.setAttribute('aria-expanded', 'true');
    });

    $searchInput.focus();

    window.addEventListener('keydown', onEscape);

    // Lock Body scroll
    disableBodyScroll($searchPanel);

    // Close panel
    down($searchPanel, { display: 'flex' });
};

$searchPanelBtn.forEach((btn) => {
    btn.addEventListener('click', (event) => {
        event.stopPropagation();
        expandSearchPanel();
    });
});

$searchPanelCloseBtn.addEventListener('click', (event) => {
    event.stopPropagation();
    collapseSearchPanel();
});

// -- Autocomplete search --//

// Config
const autoCompleteConfig = {
    minCharacters: 3, // Minimun characters
    debounceDelay: 300, // Delay in ms
};

// clear search
const clearSuggestions = () => {
    if ($suggestionsList) {
        $suggestionsList.innerHTML = '';
    }
    if ($suggestionsSection) {
        $suggestionsSection.style.display = 'none';
    }
};

const highlightTerm = (text, term) => {
    const regex = new RegExp(`(${term})`, 'gi');
    return text.replace(regex, '<strong>$1</strong>');
};

const displaySuggestions = (results, searchTerm) => {
    if (!$suggestionsList) return;

    if (!results || results.length === 0) {
        clearSuggestions();
        return;
    }

    const listItems = results
        .map(
            (result) => `
        <li>
            <svg class="icon-search" width="24" height="24">
                <use xlink:href="/wp-content/themes/fadoq/icons/symbols.svg#search" />
            </svg>
            <a href="${result.url}">
                <span>${highlightTerm(result.title, searchTerm)}</span>
                <svg class="icon-arrow-right" width="20" height="20">
                    <use xlink:href="/wp-content/themes/fadoq/icons/symbols.svg#arrow-right" />
                </svg>
            </a>
        </li>
    `,
        )
        .join('');

    $suggestionsList.innerHTML = listItems;

    // Show suggestions section when suggestions are displayed
    if ($suggestionsSection) {
        $suggestionsSection.style.display = 'block';
    }
};

const fetchSuggestions = async (searchTerm) => {
    if (!searchTerm || searchTerm.length < autoCompleteConfig.minCharacters) {
        clearSuggestions();
        return;
    }

    // Validate ajax_script exists
    if (!window.ajax_script?.ajaxurl) {
        return;
    }

    const ajaxUrl = window.ajax_script.ajaxurl;
    const response = await fetch(
        `${ajaxUrl}?action=autocomplete_search&term=${encodeURIComponent(searchTerm)}`,
        {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
            },
        },
    );

    const results = await response.json();
    displaySuggestions(results, searchTerm);
};

// -- Recent searches --//

const getlastSearch = () => {
    const searches = Cookies.get('lastSearch');
    return searches ? JSON.parse(searches) : [];
};

const displaylastSearch = () => {
    if (!$lastSearchList || !$lastSearchSection) return;

    const lastSearch = getlastSearch();

    if (lastSearch.length === 0) {
        $lastSearchSection.style.display = 'none';
        return;
    }

    $lastSearchSection.style.display = 'block';

    const listItems = lastSearch
        .map((search) => `<li><a href="${search.url}">${search.title}</a></li>`)
        .join('');

    $lastSearchList.innerHTML = listItems;
};

const saveRecentSearch = (title, url) => {
    const lastSearch = getlastSearch();

    // Remove duplicate
    const filtered = lastSearch.filter((search) => search.url !== url);

    // Last search at first
    filtered.unshift({ title, url });

    // Display/Keep last 4 searches
    const updated = filtered.slice(0, 4);

    // Save to cookie
    Cookies.set('lastSearch', JSON.stringify(updated), { expires: 365 });

    // Update display
    displaylastSearch();
};

// Init recent searches on page load
displaylastSearch();

if ($searchInput) {
    $searchInput.addEventListener('input', (event) => {
        const searchTerm = event.target.value.trim();

        // Clear previous timeout
        if (searchTimeout) {
            clearTimeout(searchTimeout);
        }

        // Debounce: wait before launching search
        searchTimeout = setTimeout(() => {
            fetchSuggestions(searchTerm);
        }, autoCompleteConfig.debounceDelay);
    });

    // Clear suggestions when input is cleared
    $searchInput.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
            clearSuggestions();
            $searchInput.value = '';
        }
    });
}

// Save search when clicking on a suggestion
if ($suggestionsList) {
    $suggestionsList.addEventListener('click', (event) => {
        const $link = event.target.closest('a');
        if (!$link) return;

        const title = $link.querySelector('span')?.textContent || '';
        const url = $link.getAttribute('href');

        if (title && url) {
            saveRecentSearch(title, url);
        }
    });
}

// Save search when clicking search button
if ($searchBtn && $searchInput) {
    $searchBtn.addEventListener('click', () => {
        const searchTerm = $searchInput.value.trim();

        if (searchTerm.length >= autoCompleteConfig.minCharacters) {
            const searchUrl = `/?s=${encodeURIComponent(searchTerm)}`;
            saveRecentSearch(searchTerm, searchUrl);
        }
    });
}
